/**
 * SDK 套餐列表请求(B6)
 *
 * 复用 SDK 请求层 post(hotelId 自动注入 body,token 可选自动带);
 * 复制 mapApiRoomToUI(纯函数,避免耦合 hotel-hub components 目录,防其后续引入重依赖污染 bundle)。
 * 套餐列表为公开接口,不需登录--带不带 token 由请求层按当前登录态决定,无副作用。
 */
import type { ApiPackageListResponse, ApiRoomType, ApiPackageItem } from '@/types/api-package';
import type { RoomType, PackageItem } from '@/types/room';
import { post } from './client';
import { toLocalDateString } from '../lib/date';

/** 复制自 src/components/packages/api-mapper.ts(纯函数,无运行时依赖) */
function mapApiPackageToUI(api: ApiPackageItem): PackageItem {
  return {
    id: api.id,
    name: api.code,
    brief: api.brief,
    breakfastType: api.breakfastType,
    breakfastNum: api.breakfastNum,
    cancelPolicy: api.cancelPolicy,
    cancelType: api.cancelType,
    lastCancelTime: api.lastCancelTime,
    payType: api.payType,
    sellState: api.sellState,
    price: api.price,
    afterCouponPrice: api.afterCouponPrice,
    discountAmount: api.discountAmount,
    minimumDescription: api.minimumDescription,
    isViewCalendar: api.isViewCalendar,
    isSellOut: api.isSellOut,
    dateStep: api.dateStep,
    dayLimitMin: api.dayLimitMin,
    dayLimitMax: api.dayLimitMax,
    packageType: api.packageType,
  };
}

function mapApiRoomToUI(api: ApiRoomType): RoomType {
  return {
    id: api.id,
    name: api.roomCode,
    description: api.description,
    images: api.picList.map((src) => ({ src, alt: api.roomCode })),
    bedDescription: api.bedDescription,
    areaInfo: api.areaInfo,
    maximumOccupancyInfo: api.maximumOccupancyInfo,
    basicInfoList: api.basicInfoList,
    facilities: Object.entries(api.facilities)
      .filter(([, items]) => items?.length)
      .map(([title, items]) => ({ title, items })),
    packages: api.packageList?.map(mapApiPackageToUI) ?? [],
  };
}

/**
 * 拉取套餐列表(hotelId 由请求层注入)。
 * @returns roomList 映射后的 RoomType[];接口异常抛 SdkError
 */
export async function fetchPackageList(checkIn: Date, checkOut: Date): Promise<RoomType[]> {
  const data = await post<ApiPackageListResponse>('/api/packages/list', {
    checkIn: toLocalDateString(checkIn),
    checkOut: toLocalDateString(checkOut),
  });
  return (data?.roomList ?? []).map(mapApiRoomToUI);
}
